import { createRouter, type RouterHistory } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import type { SessionContextValue } from '@portal/platform/session';
import { APP_CONFIG } from './config';
import { routeTree } from './routeTree.gen';

export interface RouterContext {
  queryClient: QueryClient;
  /**
   * Who is logged in and where they are scoped — what route guards (`beforeLoad`) read. Passed
   * on every render by `<App>` (src/app.tsx), so it is never stale; `<App>` also invalidates the
   * router when it changes, so every guard runs again.
   */
  session: SessionContextValue;
}

/** `history` is for tests (memory history); the app uses the browser's. */
export function createAppRouter(
  queryClient: QueryClient,
  options: { history?: RouterHistory } = {},
) {
  return createRouter({
    routeTree,
    basepath: APP_CONFIG.BASE_PATH,
    ...options,
    // `session` is supplied by <App> through RouterProvider's `context`.
    context: { queryClient, session: undefined! },
    scrollRestoration: true,
    defaultPreload: 'intent',
    // Loaders read through the query cache, which owns freshness.
    defaultPreloadStaleTime: 0,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
